import { LayoutComponent } from './layouts/layout/layout.component';
import { VexRoutes } from '@vex/interfaces/vex-route.interface';
import { AuthAdminGuard, AuthGuard } from '../shared/auth/auth.guard';

export const appRoutes: VexRoutes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/pages/auth/login/login.component').then(
        (m) => m.LoginComponent
      )
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./pages/pages/auth/register/register.component').then(
        (m) => m.RegisterComponent
      )
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import(
        './pages/pages/auth/forgot-password/forgot-password.component'
      ).then((m) => m.ForgotPasswordComponent)
  },
  {
    path: 'coming-soon',
    loadComponent: () =>
      import('./pages/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      )
  },
  {
    path: '',
    canActivate: [AuthGuard],
    component: LayoutComponent,
    children: [
      {
        path: 'dashboards/analytics',
        loadComponent: () =>
          import(
            './pages/dashboards/dashboard-analytics/dashboard-analytics.component'
          ).then((m) => m.DashboardAnalyticsComponent)
      },
      {
        path: 'dashboards/data-analysis',
        redirectTo: '/',
        pathMatch: 'full'
      },
      {
        path: '',
        loadComponent: () =>
          import(
            './pages/dashboards/dashboard-data-analysis/dashboard-data-analysis.component'
          ).then((m) => m.DashboardDataAnalysisComponent)
      },
      {
        path: 'apps',
        children: [
          {
            path: 'chat',
            loadChildren: () => import('./pages/apps/chat/chat.routes')
          },
          {
            path: 'mail',
            loadChildren: () => import('./pages/apps/mail/mail.routes'),
            data: {
              toolbarShadowEnabled: true,
              scrollDisabled: true
            }
          },
          {
            path: 'social',
            loadChildren: () => import('./pages/apps/social/social.routes')
          },
          {
            path: 'alert',
            loadChildren: () => import('./pages/apps/alert-system/alert-system.routes')
          },
          {
            path: 'contacts',
            loadChildren: () => import('./pages/apps/contacts/contacts.routes')
          },
          {
            path: 'calendar',
            loadComponent: () =>
              import('./pages/apps/calendar/calendar.component').then(
                (m) => m.CalendarComponent
              ),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            path: 'aio-table',
            loadComponent: () =>
              import('./pages/apps/aio-table/aio-table.component').then(
                (m) => m.AioTableComponent
              ),
            data: {
              toolbarShadowEnabled: false
            }
          },
          {
            path: 'ads',
            loadChildren: () => import('./pages/apps/ads/ads.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'orders',
            loadChildren: () => import('./pages/apps/orders/orders.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'social-publisher',
            loadChildren: () => import('./pages/apps/social-publisher/social-publisher.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'billboard',
            loadChildren: () => import('./pages/apps/billboard/billboard.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'design-studio',
            loadChildren: () => import('./pages/apps/design-studio/design-studio.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'payments-report',
            loadChildren: () => import('./pages/apps/payments-report/payments-report.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            // SCRUM-259 — business accounts (VIP): license review + settings
            path: 'business-accounts',
            loadChildren: () => import('./pages/apps/business-accounts/business-accounts.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            // In-site inquiries → WhatsApp relay: settings, delivery log, Meta setup (superusers)
            path: 'whatsapp-inquiries',
            loadChildren: () => import('./pages/apps/whatsapp-inquiries/whatsapp-inquiries.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            // SCRUM-324 — coupons admin ("ניהול קופונים"); superusers only
            path: 'coupons',
            canActivate: [AuthAdminGuard],
            loadChildren: () => import('./pages/apps/coupons/coupons.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            // SEC-08 (SCRUM-353) — privacy requests register ("פניות פרטיות"); superusers only
            path: 'privacy-requests',
            canActivate: [AuthAdminGuard],
            loadChildren: () => import('./pages/apps/privacy-requests/privacy-requests.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            // SCRUM-196 — Messaging Center (SMS via our backend; superusers only)
            path: 'messaging',
            loadChildren: () => import('./pages/apps/messaging/messaging.routes'),
            data: { toolbarShadowEnabled: false }
          },
          {
            path: 'help-center',
            loadChildren: () =>
              import('./pages/apps/help-center/help-center.routes')
          },
          {
            path: 'scrumboard',
            loadChildren: () =>
              import('./pages/apps/scrumboard/scrumboard.routes')
          },
          {
            path: 'editor',
            loadComponent: () =>
              import('./pages/apps/editor/editor.component').then(
                (m) => m.EditorComponent
              ),
            data: {
              scrollDisabled: true
            }
          }
        ]
      },
      {
        path: 'ads-manager',
        loadComponent: () =>
          import('./pages/ads-manager/ads-manager.component').then(
            (m) => m.AdsManagerComponent
          ),
        data: { toolbarShadowEnabled: false }
      },
      {
        // Top-level section of its own — these are reference-data tables, not
        // applications, so they don't sit under /apps.
        path: 'tables',
        loadChildren: () => import('./pages/tables/tables.routes'),
        data: { toolbarShadowEnabled: false }
      },
      {
        path: 'pages',
        children: [
          {
            path: 'pricing',
            loadComponent: () =>
              import('./pages/pages/pricing/pricing.component').then(
                (m) => m.PricingComponent
              )
          },
          {
            path: 'faq',
            loadComponent: () =>
              import('./pages/pages/faq/faq.component').then(
                (m) => m.FaqComponent
              )
          },
          {
            path: 'guides',
            loadComponent: () =>
              import('./pages/pages/guides/guides.component').then(
                (m) => m.GuidesComponent
              )
          },
          {
            path: 'invoice',
            loadComponent: () =>
              import('./pages/pages/invoice/invoice.component').then(
                (m) => m.InvoiceComponent
              )
          },
          {
            path: 'error-404',
            loadComponent: () =>
              import('./pages/pages/errors/error-404/error-404.component').then(
                (m) => m.Error404Component
              )
          },
          {
            path: 'error-500',
            loadComponent: () =>
              import('./pages/pages/errors/error-500/error-500.component').then(
                (m) => m.Error500Component
              )
          }
        ]
      },
      {
        path: 'ui',
        children: [
          {
            path: 'components',
            loadChildren: () =>
              import('./pages/ui/components/components.routes')
          },
          {
            path: 'forms/form-elements',
            loadComponent: () =>
              import(
                './pages/ui/forms/form-elements/form-elements.component'
              ).then((m) => m.FormElementsComponent)
          },
          {
            path: 'forms/form-wizard',
            loadComponent: () =>
              import('./pages/ui/forms/form-wizard/form-wizard.component').then(
                (m) => m.FormWizardComponent
              )
          },
          {
            path: 'icons',
            loadChildren: () => import('./pages/ui/icons/icons.routes')
          },
          {
            path: 'page-layouts',
            loadChildren: () =>
              import('./pages/ui/page-layouts/page-layouts.routes')
          }
        ]
      },
      {
        path: 'documentation',
        loadChildren: () => import('./pages/documentation/documentation.routes')
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/pages/errors/error-404/error-404.component').then(
            (m) => m.Error404Component
          )
      }
    ]
  }
];
